import api from './api';
import type { LocationLite } from '../types/locations';

/**
 * An existing location matching a looked-up address. Edit-ready (same shape the
 * edit drawer consumes) plus `delivery_type` (DIRECT/WHSE) for the dupe popup.
 */
export interface DupeLocation {
  id: number;
  lname: string;
  ladd: string;
  lcity: string;
  lstate: string;
  lzip: string;
  lastuseddatetime: string | null;
  deleted: string;
  delivery_type: string | null;
  [key: string]: any;
}

/**
 * Live server-side location typeahead (`GET /admin/locations?q=`), min 2
 * chars (shorter terms return []). Two match profiles:
 * - default (broad): lname / cyalias / lcity / ladd contains — leg stops,
 *   consignee, CSX terminal picker.
 * - `match: 'name'`: lname contains + cyalias exact (+ `address` for the two
 *   street lines) — the quote-wizard / Change Template / tablemaint pickers'
 *   name-first semantics (former /quotes/location-lookup).
 * All-digit terms also match recno exactly, sorted first.
 * Deliberately NOT routed through cachedGet — a search must see locations
 * created seconds ago (the whole point vs. the old preloaded full list).
 */
export async function searchLocations(
  q: string,
  opts?: { signal?: AbortSignal; limit?: number; match?: 'name'; address?: boolean }
): Promise<LocationLite[]> {
  const res = await api.get<LocationLite[]>('/admin/locations', {
    params: {
      q,
      ...(opts?.limit ? { limit: opts.limit } : {}),
      ...(opts?.match ? { match: opts.match } : {}),
      ...(opts?.address ? { address: 1 } : {}),
    },
    signal: opts?.signal,
  });
  return res.data || [];
}

/** Find existing locations with the same ladd/lcity/lstate/lzip (incl. deleted). */
export async function dupeCheckLocations(
  address: string,
  city: string,
  state: string,
  zip: string
): Promise<DupeLocation[]> {
  const res = await api.get('/locations/dupe-check', { params: { address, city, state, zip } });
  return res.data?.data || [];
}
